// src/encounter/ips-procedure-dialog.tsx
// Add/Edit dialog for the IPS-rail Procedures section (PROD-1266). PRESENTATIONAL — composes
// DS Modal + CodeSearchField (CPT procedure) + DatePicker (procedure date) + TextField (optional
// note) + Button. No hooks/network: the host supplies search results + the submit/search
// callbacks (mirrors IpsConditionDialog / IpsAllergyDialog, MINUS the status/severity Selects).
// On submit it resolves the selection into a ProcedureFormValue (code reads displayName, date →
// onsetDateTime ISO-no-Z + onsetDateTimeDisplay); the rail turns that into a wire request via
// buildProcedureRequest + useSaveProcedure.
import { useState } from 'react';
import {
  Button,
  CodeSearchField,
  TextField,
  HelperText,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import type { CodeDisplayName, PatientProcedure } from '@/types/patient-chart-api';
import { ReportedDateField } from './reported-date-field';
import { resolveReportedDate, seedReportedDate } from './lib/reported-date';
import type { ProcedureFormValue } from '@/encounter/lib/to-procedure-request';

export interface IpsProcedureDialogProps {
  open: boolean;
  /** Present → Edit mode; absent → Add mode. */
  editing?: PatientProcedure | null;

  // Procedure search (host-owned).
  query: string;
  onQueryChange: (next: string) => void;
  results: CodeSearchResult[];
  searching?: boolean;
  /** Resolve a clicked result id → the full CPT CodeDisplayName row (reads displayName). */
  resolveProcedure: (result: CodeSearchResult) => CodeDisplayName | null;

  saving?: boolean;
  onSubmit: (value: ProcedureFormValue) => void;
  onClose: () => void;
}

/** THE ruled field-label treatment (PROD-1801, Andrew 2 Aug) — shared, not re-declared.
 *  These six dialogs each carried a byte-identical local copy at xs/600/0.08em, which is a
 *  seventh idiom next to the four the rail popover already showed at once. The margin is the
 *  only local part: `AP_FIELD_LABEL` sets no spacing, and these labels sit above their field. */
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
// literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the
// equivalent class string rather than imported as a style object.
const FIELD_LABEL =
  'pa-ips-label block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const SELECTED_CHIP =
  'pa-ips-chip flex items-center justify-between gap-2 py-2 px-3 border border-solid border-border-default rounded-md bg-surface-card';
/** Procedure-code display reads `displayName` (PascalCase), NOT `display`. */
function codeLabel(c: CodeDisplayName): string {
  return (c.displayName || c.description || c.shortName || c.code || '—').trim();
}

/** Seed the partial-date control from an existing record.
 *
 *  Flutter's procedure date is a DynamicDatePicker (`proc_tab.dart:317`), whose helper text reads
 *  "Enter date (DD/MM/YYYY, MM/YYYY, YYYY or duration eg: 1Y or 2M or 3D)" — so "3y" is a
 *  first-class value. The wire has always carried the pair: `onsetDateTimeDisplay` holds what the
 *  user typed VERBATIM, `onsetDateTime` the resolved instant. React collected only a full calendar
 *  date and REFORMATTED it to DD/MM/YYYY, discarding the verbatim text and making "3y" unenterable.
 *
 *  On edit we prefer the stored display string, so stored text round-trips unchanged. Since the
 *  owner answers on PR #843 a NEW duration is stored as "about YYYY, reported … as N years ago" at
 *  year precision, and a stored "Unknown" seeds the Unknown choice (`lib/reported-date.ts`). */
function seedDate(existing: PatientProcedure | null | undefined) {
  return seedReportedDate(existing?.onsetDateTimeDisplay, existing?.onsetDateTime);
}

/** Outer gate + keyed remount; the inner form initialises state from `editing` via lazy
 *  useState initialisers (no setState-in-effect). */
export function IpsProcedureDialog(props: IpsProcedureDialogProps) {
  if (!props.open) return null;
  return <IpsProcedureDialogForm key={props.editing?.identifier ?? 'add'} {...props} />;
}

function IpsProcedureDialogForm({
  editing = null,
  query,
  onQueryChange,
  results,
  searching,
  resolveProcedure,
  saving,
  onSubmit,
  onClose,
}: IpsProcedureDialogProps) {
  const isEditing = !!editing?.identifier;
  const [code, setCode] = useState<CodeDisplayName | null>(() => editing?.code ?? null);
  const [date, setDate] = useState(() => seedDate(editing));
  const [note, setNote] = useState<string>(() => editing?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    if (!code) {
      setError('Pick a procedure.');
      return;
    }
    // The date is OPTIONAL (encounter owner ruling, 1 Nov 2026: year, month-year, full date or
    // unknown, not required as a full date). A duration is stored at year precision and an
    // explicit Unknown as the word "Unknown" with no timestamp (owner answers on PR #843); blank
    // sends no display text. Typed text that does not resolve is still an error, never dropped.
    // `resolveReportedDate` keeps a stored timestamp that had no display text (Codex P1, round 1).
    const resolved = resolveReportedDate(date);
    if (!resolved.ok) {
      setError('That date could not be read. Try DD/MM/YYYY, MM/YYYY, YYYY, or a duration like 3Y.');
      return;
    }
    onSubmit({
      code,
      onsetDateTime: resolved.iso,
      onsetDateTimeDisplay: resolved.display,
      note: note.trim() || null,
    });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit procedure' : 'Add procedure'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save procedure',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        {/* Procedure (CPT) */}
        <div className={FIELD_GROUP}>
          <span className={FIELD_LABEL}>Procedure</span>
          {code ? (
            <div className={SELECTED_CHIP}>
              <span className="text-size-sm text-text-primary">
                {codeLabel(code)}
                {code.code ? (
                  <span className="text-text-hint"> · {code.code}</span>
                ) : null}
              </span>
              <Button variant="tertiary" size="sm" onClick={() => setCode(null)} disabled={saving}>
                Change
              </Button>
            </div>
          ) : (
            <CodeSearchField
              value={query}
              onChange={onQueryChange}
              results={results}
              loading={searching}
              placeholder="Search CPT procedures…"
              onResultClick={(r) => {
                const picked = resolveProcedure(r);
                if (picked) {
                  setCode(picked);
                  setError(null);
                }
              }}
            />
          )}
        </div>

        {/* Procedure date — optional; a date, a duration (year precision) or Unknown. */}
        <div className={FIELD_GROUP}>
          <ReportedDateField label="Procedure date" value={date} onChange={setDate} disabled={saving} />
        </div>

        {/* Note (optional) */}
        <div className={FIELD_GROUP}>
          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional context…"
            disabled={saving}
          />
        </div>

        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
